import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle, History, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { timeEntryAPI, toLocalDateString } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { TASKS, formatHours, hoursToTimeStr, timeStrToHours } from '../utils/timeEntries';

const PERIODS = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
];
const ALL = 'tous';

const errorText = (error) => error?.response?.data?.detail || error?.message || String(error);
// [id, nom] des salariés ou chantiers présents dans les pointages, pour les filtres.
const filterOptions = (entries, idKey, nameKey) => [...new Map(entries.filter((e) => e[idKey]).map((e) => [e[idKey], e[nameKey]])).entries()]
  .sort((a, b) => a[1].localeCompare(b[1], 'fr'));
const longDate = (iso) => format(parseISO(iso), 'EEEE d MMMM', { locale: fr });

// Correction d'un pointage : les champs de la saisie, pour un seul salarié.
const EditEntryDialog = ({ entry, projects, employees, onClose, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(entry ? {
      date: entry.date,
      projectId: entry.projectId || '',
      employeeId: entry.employeeId || '',
      task: entry.task,
      time: hoursToTimeStr(entry.hours),
      comment: entry.comment,
      problem: entry.problem,
    } : null);
  }, [entry]);

  // Chantiers terminés compris : on corrige aussi des heures passées.
  const projectChoices = useMemo(() => [...projects]
    .sort((a, b) => (a.status === 'completed') - (b.status === 'completed') || a.name.localeCompare(b.name, 'fr')),
  [projects]);
  const employeeChoices = useMemo(() => [...employees]
    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'fr')),
  [employees]);

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));
  const hours = form ? timeStrToHours(form.time) : 0;
  const tasks = form?.task && !TASKS.includes(form.task) ? [form.task, ...TASKS] : TASKS;
  const canSave = Boolean(form && form.date && form.projectId && form.employeeId && form.task && hours > 0 && hours <= 24);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await timeEntryAPI.update(entry.id, {
        date: form.date,
        projectId: form.projectId,
        employeeId: form.employeeId,
        task: form.task,
        hours,
        comment: form.comment || null,
        problem: form.problem,
      });
      toast({
        title: 'Pointage corrigé',
        duration: 5000,
      });
      onSaved();
    } catch (error) {
      toast({ title: 'Correction impossible', description: errorText(error), variant: 'destructive', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifier le pointage</DialogTitle>
          <DialogDescription>La correction remplace la saisie d'origine.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-entry-date">Date</Label>
                <Input id="edit-entry-date" type="date" value={form.date}
                       onChange={(e) => setField('date')(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-entry-time">Heures</Label>
                <Input id="edit-entry-time" type="time" value={form.time}
                       onChange={(e) => setField('time')(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Salarié</Label>
              <Select value={form.employeeId} onValueChange={setField('employeeId')}>
                <SelectTrigger><SelectValue placeholder="Choisir un salarié" /></SelectTrigger>
                <SelectContent>
                  {employeeChoices.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.fullName}{e.active === false ? ' (inactif)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Chantier</Label>
              <Select value={form.projectId} onValueChange={setField('projectId')}>
                <SelectTrigger><SelectValue placeholder="Choisir un chantier" /></SelectTrigger>
                <SelectContent>
                  {projectChoices.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.status === 'completed' ? ' (terminé)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tâche</Label>
              <Select value={form.task} onValueChange={setField('task')}>
                <SelectTrigger><SelectValue placeholder="Choisir une tâche" /></SelectTrigger>
                <SelectContent>
                  {tasks.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-entry-comment">Commentaire</Label>
              <Textarea id="edit-entry-comment" rows={2} value={form.comment}
                        onChange={(e) => setField('comment')(e.target.value)} />
            </div>
            <label htmlFor="edit-entry-problem" className="flex items-center space-x-2 cursor-pointer">
              <Checkbox id="edit-entry-problem" checked={form.problem}
                        onCheckedChange={(value) => setField('problem')(value === true)} />
              <span className="text-sm flex items-center">
                <AlertTriangle className="w-3.5 h-3.5 mr-1 text-orange-500" />
                Problème à signaler
              </span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={save} disabled={!canSave || saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dernières saisies d'heures, lues dans l'app : consulter, corriger, supprimer.
const TimeEntryList = ({ projects, employees, refreshKey, onChanged }) => {
  const { toast } = useToast();
  const [period, setPeriod] = useState('30');
  const [employeeFilter, setEmployeeFilter] = useState(ALL);
  const [projectFilter, setProjectFilter] = useState(ALL);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const since = toLocalDateString(subDays(new Date(), Number(period)));
      const data = await timeEntryAPI.list({ since });
      setEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({ title: 'Pointages illisibles', description: errorText(error), variant: 'destructive', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [period, toast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const afterChange = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const confirmDelete = async () => {
    const entry = deleting;
    setRemoving(true);
    try {
      await timeEntryAPI.remove(entry.id);
      toast({
        title: 'Pointage supprimé',
        description: `${entry.employeeName}, ${longDate(entry.date)}.`,
        duration: 5000,
      });
      setDeleting(null);
      await afterChange();
    } catch (error) {
      toast({ title: 'Suppression impossible', description: errorText(error), variant: 'destructive', duration: 6000 });
    } finally {
      setRemoving(false);
    }
  };

  const employeeOptions = useMemo(() => filterOptions(entries, 'employeeId', 'employeeName'), [entries]);
  const projectOptions = useMemo(() => filterOptions(entries, 'projectId', 'projectName'), [entries]);
  const visible = entries.filter((e) => (employeeFilter === ALL || e.employeeId === employeeFilter)
    && (projectFilter === ALL || e.projectId === projectFilter));
  const total = visible.reduce((sum, e) => sum + (e.hours || 0), 0);

  return (
    <Card className="border-blue-200">
      <CardHeader className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center text-blue-800">
            <History className="w-5 h-5 mr-2" />
            Dernières saisies
          </CardTitle>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44 border-blue-200" aria-label="Période"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger className="w-48 border-blue-200" aria-label="Salarié"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les salariés</SelectItem>
              {employeeOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-56 border-blue-200" aria-label="Chantier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous les chantiers</SelectItem>
              {projectOptions.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-blue-700 ml-auto tabular-nums">
            {visible.length} pointage{visible.length > 1 ? 's' : ''} · {formatHours(total)}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Chargement des pointages…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">Aucun pointage sur la période.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Salarié</TableHead>
                  <TableHead>Chantier</TableHead>
                  <TableHead>Tâche</TableHead>
                  <TableHead className="text-right">Heures</TableHead>
                  <TableHead>Commentaire</TableHead>
                  <TableHead>Saisie</TableHead>
                  <TableHead className="text-right"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="whitespace-nowrap capitalize">
                      {format(parseISO(entry.date), 'EEE d MMM', { locale: fr })}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{entry.employeeName}</TableCell>
                    <TableCell className="max-w-[14rem] truncate" title={entry.projectName}>{entry.projectName}</TableCell>
                    <TableCell className="whitespace-nowrap">{entry.task}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{formatHours(entry.hours)}</TableCell>
                    <TableCell className="max-w-[16rem]">
                      <div className="flex items-center gap-1">
                        {entry.problem && (
                          <AlertTriangle className="w-3.5 h-3.5 text-orange-500 shrink-0" aria-label="Problème signalé" />
                        )}
                        <span className="truncate" title={entry.comment}>{entry.comment}</span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-gray-600">
                      {entry.enteredWith === 'Planning App' ? 'App' : 'AppSheet (ancien)'}
                      {entry.appEditedAt && <span className="ml-1 text-blue-600">· corrigé</span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(entry)}
                              aria-label={`Modifier le pointage de ${entry.employeeName} du ${longDate(entry.date)}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                              onClick={() => setDeleting(entry)}
                              aria-label={`Supprimer le pointage de ${entry.employeeName} du ${longDate(entry.date)}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <EditEntryDialog
        entry={editing}
        projects={projects}
        employees={employees}
        onClose={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await afterChange(); }}
      />

      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => { if (!open && !removing) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce pointage ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${deleting.employeeName} — ${formatHours(deleting.hours)} le ${longDate(deleting.date)} sur ${deleting.projectName} (${deleting.task}). `}
              L'app garde la trace du pointage supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={removing}
                               className="bg-red-600 hover:bg-red-700">
              {removing ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TimeEntryList;
